/**
 * Validações para Telas Institucionais
 * Regras de negócio do sistema
 */

export interface ValidacaoResultado {
  valido: boolean;
  mensagens?: string[];
}

/**
 * ✅ Validar se pelo menos 1 tela está marcada como "SIM"
 */
export const validarPeloMenosUmaTela = (
  alterarTelaInicial: string,
  alterarTelaAguarde: string,
  alterarTelaAbertura: string
): ValidacaoResultado => {
  if (
    alterarTelaInicial !== 'sim' &&
    alterarTelaAguarde !== 'sim' &&
    alterarTelaAbertura !== 'sim'
  ) {
    return {
      valido: false,
      mensagens: ['Selecione pelo menos uma tela para alterar (Sim)']
    };
  }

  return { valido: true };
};

/**
 * ✅ Validar tempo de exibição (1-30 segundos)
 */
export const validarTempoExibicao = (tempo: number): ValidacaoResultado => {
  if (!tempo || tempo < 1 || tempo > 30) {
    return {
      valido: false,
      mensagens: ['Tempo de exibição deve estar entre 1 e 30 segundos']
    };
  }

  return { valido: true };
};

/**
 * ✅ Validar Telas Iniciais
 * Regra: Se "Deseja Alterar" = SIM, precisa de PELO MENOS 1 imagem
 */
export const validarTelasIniciais = (
  alterarTelaInicial: string,
  telasIniciais: any[],
  imagensPorPosicao: Record<number, { file: File | null; url: string | null }>
): ValidacaoResultado => {
  const mensagens: string[] = [];

  if (alterarTelaInicial !== 'sim') {
    return { valido: true };
  }

  // ✅ Verificar se tem PELO MENOS 1 imagem
  const temAlgumaImagem = [1, 2, 3, 4].some(pos => !!imagensPorPosicao[pos]?.file);

  if (!temAlgumaImagem) {
    mensagens.push('Telas Iniciais: Selecione pelo menos 1 imagem');
  }

  // ✅ Se tem imagem, validar tempo para cada tela com imagem
  for (const tela of telasIniciais) {
    const temImagem = !!imagensPorPosicao[tela.posicao]?.file;

    if (temImagem) {
      const validacaoTempo = validarTempoExibicao(tela.tempo);
      if (!validacaoTempo.valido) {
        mensagens.push(`${tela.tela}: ${validacaoTempo.mensagens?.[0]}`);
      }
    }
  }

  if (mensagens.length > 0) {
    return { valido: false, mensagens };
  }

  return { valido: true };
};

/**
 * ✅ Validar Telas de Aguarde
 * Regra: Se "Deseja Alterar" = SIM, precisa de PELO MENOS 1 imagem (Geral OU Chip)
 */
export const validarTelasAguarde = (
  alterarTelaAguarde: string,
  imagensPorPosicao: Record<number, { file: File | null; url: string | null }>
): ValidacaoResultado => {
  if (alterarTelaAguarde !== 'sim') {
    return { valido: true };
  }

  // ✅ Validar se tem PELO MENOS 1 imagem (Geral OU Chip)
  const temImagemGeral = !!imagensPorPosicao[6]?.file;
  const temImagemChip = !!imagensPorPosicao[7]?.file;

  if (!temImagemGeral && !temImagemChip) {
    return {
      valido: false,
      mensagens: ['Telas de Aguarde: Selecione pelo menos 1 imagem (Geral ou Chip)']
    };
  }

  return { valido: true };
};

/**
 * ✅ Validar Tela de Abertura
 * Regra: 
 * - Se "Deseja Alterar" = SIM e "Apresenta para Cliente" = SIM → Imagem OBRIGATÓRIA
 * - Se "Deseja Alterar" = SIM e "Apresenta para Cliente" = NÃO → Imagem OPCIONAL
 */
export const validarTelaAberturaCompleta = (
  alterarTelaAbertura: string,
  apresentaParaCliente: string,
  imagensPorPosicao: Record<number, { file: File | null; url: string | null }>
): ValidacaoResultado => {
  if (alterarTelaAbertura !== 'sim') {
    return { valido: true };
  }

  const temImagem = !!imagensPorPosicao[5]?.file;

  // ✅ Se "Apresenta para Cliente" = SIM, imagem é OBRIGATÓRIA
  if (apresentaParaCliente === 'sim' && !temImagem) {
    return {
      valido: false,
      mensagens: ['Tela de Abertura: Imagem obrigatória quando "Apresenta para Cliente" = Sim']
    };
  }

  // ✅ Se "Apresenta para Cliente" = NÃO, imagem é OPCIONAL (sem erro)
  return { valido: true };
};

/**
 * ✅ VALIDAÇÃO COMPLETA - Executar todas as validações
 * Retorna TODAS as mensagens de erro de uma vez
 */
export const validarTodosCadastro = (
  alterarTelaInicial: string,
  alterarTelaAguarde: string,
  alterarTelaAbertura: string,
  apresentaParaCliente: string,
  telasIniciais: any[],
  imagensPorPosicao: Record<number, { file: File | null; url: string | null }>
): ValidacaoResultado => {
  const todasAsMensagens: string[] = [];

  // 1. Validar se pelo menos 1 tela está marcada como SIM
  let resultado = validarPeloMenosUmaTela(
    alterarTelaInicial,
    alterarTelaAguarde,
    alterarTelaAbertura
  );
  if (!resultado.valido) {
    todasAsMensagens.push(...(resultado.mensagens || []));
  }

  // 2. Validar Telas Iniciais
  resultado = validarTelasIniciais(alterarTelaInicial, telasIniciais, imagensPorPosicao);
  if (!resultado.valido) {
    todasAsMensagens.push(...(resultado.mensagens || []));
  }

  // 3. Validar Telas de Aguarde
  resultado = validarTelasAguarde(alterarTelaAguarde, imagensPorPosicao);
  if (!resultado.valido) {
    todasAsMensagens.push(...(resultado.mensagens || []));
  }

  // 4. Validar Tela de Abertura
  resultado = validarTelaAberturaCompleta(
    alterarTelaAbertura,
    apresentaParaCliente,
    imagensPorPosicao
  );
  if (!resultado.valido) {
    todasAsMensagens.push(...(resultado.mensagens || []));
  }

  // ✅ Se tem mensagens, retorna TODAS de uma vez
  if (todasAsMensagens.length > 0) {
    return { valido: false, mensagens: todasAsMensagens };
  }

  return { valido: true };
};
